import { useCallback, useEffect, useState } from 'react';

interface UseFullscreenReturn {
  isSupported: boolean;
  isFullscreen: boolean;
  toggle: () => Promise<void>;
  exit: () => Promise<void>;
}

interface WebkitDocument {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
}

interface WebkitElement {
  webkitRequestFullscreen?: () => Promise<void>;
}

const wkDoc = document as unknown as WebkitDocument;

const isSupported =
  typeof document !== 'undefined' && (document.fullscreenEnabled || wkDoc.webkitFullscreenEnabled === true);

export function useFullscreen(ref: React.RefObject<HTMLElement | null>): UseFullscreenReturn {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      const fsElement = document.fullscreenElement ?? wkDoc.webkitFullscreenElement ?? null;
      setIsFullscreen(fsElement === ref.current && fsElement !== null);
    };

    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
    };
  }, [ref]);

  const enter = useCallback(async () => {
    const el = ref.current;
    if (!el) return;
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else {
        await (el as unknown as WebkitElement).webkitRequestFullscreen?.();
      }
    } catch {
      // User denied or API error
    }
  }, [ref]);

  const exit = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else {
        await wkDoc.webkitExitFullscreen?.();
      }
    } catch {
      // Not in fullscreen or API error
    }
  }, []);

  const toggle = useCallback(async () => {
    if (isFullscreen) await exit();
    else await enter();
  }, [isFullscreen, enter, exit]);

  return { isSupported, isFullscreen, toggle, exit };
}
